import axios, { AxiosResponse } from 'axios';
import Member from '../structures/member';
import Place from '../structures/place';
import HistoryObject from '../structures/historyObject';
import Project from '../structures/project';
import ProjectPart from '../structures/projectPart';
import Fragment from '../structures/fragment';
import UserInfo from '../structures/userInfo';
import { getToken } from './authHelper';
import Message, { MessageType } from '../structures/message';

export const baseURL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : `https://${window.location.hostname}`;

const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
    'x-access-token': getToken()
  },
})

interface VkAuthorizeResponse {
    token: string
}
interface VkAuthorizeData {
    access_token: string,
    expires_in: number,
    id_token: string,
    refresh_token: string,
    scope: string,
    state: string,
    token_type: string,
    user_id: number
}

interface GenerateImageResponse {
    id: string
}
interface GetImageStateResponse {
    status: string
}

interface GetDialogDescriptionExamplesContext{
    projectId: string; 
    members: string[];
    place: string;
}
interface GetHeroDescriptionExamplesForFragmentContext{
    projectId: string; 
    hero: string;
}
export interface TitleAndTextResponse {
    title: string
    text: string
}
export interface HeroNameAndRole {
    name: string
    role: string
}
export interface FragmentFinalContent {
    id: string
    content: string
}
export const apiRequest = async <T>(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', data?: any): Promise<T> => {
    let headers = {
        'Content-Type': 'application/json'
    } as any;
    let token = getToken();
    if(token !== undefined && token != null){
        headers = {
            'Content-Type': 'application/json',
            'x-access-token': getToken()
          };
    }
    const response: AxiosResponse<T> = await apiClient({
      method,
      url,
      data,
      headers: headers
    });
  
    return response.data;
  };

const api = {
    createProject(
                author:string, 
                name:string, 
                styleTypeForm:string, 
                styleType:string[], 
                comedy:number, 
                tragedyc:number, 
                drame:number, 
                scary:number, 
                artistry:number, 
                description:string,
                members:Member[],
                places:Place[],
                objects:HistoryObject[],
    ):Promise<Project> {
        return apiRequest<Project>('/api/createProject', 'POST', {
            author, 
            name, 
            styleTypeForm, 
            styleType, 
            comedy, 
            tragedyc, 
            drame, 
            scary, 
            artistry, 
            description,
            members,
            places,
            objects,
        });
    },
    vkAuthorize(data: VkAuthorizeData):Promise<VkAuthorizeResponse> {
        return apiRequest<VkAuthorizeResponse>('/api/vkAuthorize', 'POST', data);
    },
    getUserInfo():Promise<UserInfo> {
        return apiRequest<UserInfo>('/api/getUserInfo', 'GET');
    },
    getProjects():Promise<Project[]> {
        return apiRequest<Project[]>('/api/projects', 'GET');
    },
    generateImage(prompt: string):Promise<GenerateImageResponse> {
        return apiRequest<GenerateImageResponse>('/api/generateImage', 'POST', {prompt});
    },
    getImageState(id: string):Promise<GetImageStateResponse> {
        return apiRequest<GetImageStateResponse>(`/api/getImageState?id=${id}`, 'GET');
    },
    getImageUrl(id: string):string {
        return `${baseURL}/api/generatedImage/${id}`;
    },
    getProjectNameExamplesByUserName(userName: string):Promise<string[]> {
        return apiRequest<string[]>(`/api/getProjectNameExamplesByUserName?userName=${userName}`, 'GET');
    },
    getDialogDescriptionExamples(context:GetDialogDescriptionExamplesContext):Promise<TitleAndTextResponse[]> {
        return apiRequest<TitleAndTextResponse[]>(`/api/getDialogDescriptionExamples`, 'POST', context);
    },
    getProjectDescriptionExamplesByName(name: string):Promise<TitleAndTextResponse[]> {
        return apiRequest<TitleAndTextResponse[]>(`/api/getProjectDescriptionExamplesByName?name=${name}`, 'GET');
    },
    inventMembersNamesAndRolesOfHistory(storyDescription: string):Promise<HeroNameAndRole[]> {
        return apiRequest<HeroNameAndRole[]>(`/api/inventMembersNamesAndRolesOfHistory`, 'POST', {storyDescription});
    },
    inventContextForHero(name:string, role:string, storyDescription: string):Promise<string> {
        return apiRequest<string>(`/api/inventContextForHero`, 'POST', {name, role, storyDescription});
    },
    inventPromtForHeroPhoto(member:Member):Promise<string> {
        return apiRequest<string>(`/api/inventPromtForHeroPhoto`, 'POST', {name: member.name, role: member.role, context: member.context});
    },
    inventPlacesForHistory(storyDescription: string):Promise<string[]> {
        return apiRequest<string[]>(`/api/inventPlacesForHistory`, 'POST', {storyDescription});
    },
    inventDescriptionForPlace(name:string, storyDescription: string):Promise<string> {
        return apiRequest<string>(`/api/inventDescriptionForPlace`, 'POST', {name, storyDescription});
    },
    inventPromtForPlacePhoto(place:Place):Promise<string> {
        return apiRequest<string>(`/api/inventPromtForPlacePhoto`, 'POST', {name: place.name, description: place.description});
    },
    inventObjectsForHistory(storyDescription: string):Promise<string[]> {
        return apiRequest<string[]>(`/api/inventObjectsForHistory`, 'POST', {storyDescription});
    },
    inventDescriptionForObject(name:string, storyDescription: string):Promise<string> {
        return apiRequest<string>(`/api/inventDescriptionForObject`, 'POST', {name, storyDescription});
    },
    inventPromtForObjectPhoto(place:Place):Promise<string> {
        return apiRequest<string>(`/api/inventPromtForObjectPhoto`, 'POST', {name: place.name, description: place.description});
    },
    createPart(projectId:string, name:string, description:string):Promise<ProjectPart> {
        return apiRequest<ProjectPart>(`/api/createPart`, 'POST', {projectId, name, description});
    },
    getProjectParts(projectId:string):Promise<ProjectPart[]> {
        return apiRequest<ProjectPart[]>(`/api/projectPartsForProject/${projectId}`, 'GET');
    },
    inventParts(projectId:string):Promise<string[]> {
        return apiRequest<string[]>(`/api/inventParts`, 'POST', {projectId});
    },

    
    getMessagesForDialog(projectId:string, partId:string, fragmentId:string):Promise<Message[]> {
        return apiRequest<Message[]>(`/api/getMessagesForDialog?projectId=${projectId}&partId=${partId}&fragmentId=${fragmentId}`, 'GET');
    },
    addMessageInDialog(projectId:string, partId:string, fragmentId:string, memberId:string, message:string, type: MessageType):Promise<string> {
        return apiRequest<string>(`/api/addMessageInDialog`, 'POST', {projectId, partId, fragmentId, memberId, message, type});
    },
    determineWhoContinueDialog(projectId:string, partId:string, fragmentId:string):Promise<string> {
        return apiRequest<string>(`/api/determineWhoContinueDialog`, 'POST', {projectId, partId, fragmentId});
    },
    getNextMessage(projectId:string, partId:string, fragmentId:string, memberId:string):Promise<string> {
        return apiRequest<string>(`/api/getNextMessage`, 'POST', {projectId, partId, fragmentId, memberId});
    },
    createFragment(projectId:string, partId:string, type:string, description:string, members:string[], places:string[], objects:string[]):Promise<Fragment> {
        return apiRequest<Fragment>(`/api/createFragment`, 'POST', {
            projectId,
            partId,
            type,
            description,
            members,
            places,
            objects,
        });
    },
    getFragments(partId:string):Promise<Fragment[]> {
        return apiRequest<Fragment[]>(`/api/getFragments/${partId}`, 'GET');
    },
    updateFragmentFinishContent(projectId:string, partId:string, fragmentId:string, finishContent:string):Promise<string> {
        return apiRequest<string>(`/api/updateFragmentFinishContent`, 'POST', {projectId, partId, fragmentId, finishContent});
    },
    inventFinishContentForFragment(projectId:string, partId:string, fragmentId:string):Promise<string> {
        return apiRequest<string>(`/api/inventFinishContentForFragment`, 'POST', {projectId, partId, fragmentId});
    },
    getFinalContentForPart(projectId:string, partId:string):Promise<FragmentFinalContent[]> {
        return apiRequest<FragmentFinalContent[]>(`/api/getFinalContentForPart/${projectId}/${partId}`, 'GET');
    },
    getHeroDescriptionExamplesForFragment(context:GetHeroDescriptionExamplesForFragmentContext):Promise<TitleAndTextResponse[]> {
        return apiRequest<TitleAndTextResponse[]>(`/api/getHeroDescriptionExamplesForFragment`, 'POST', context);
    },
}

export default api;